import type { MutableRefObject } from "react";
import { useEffect, useRef, useState } from "react";
import {
  StyledActiveTriggerLogoWrapper,
  StyledButtonDisabledTriggerActionMenu,
  StyledButtonOpenTriggerActionMenu,
  StyledIntegrationLabel,
  StyledActionLabel,
} from "../style";
import {
  StyledAutomationHeader,
  StyledAutomationHeaderActions,
  StyledAutomationHeaderBody,
  StyledAutomationHeaderText,
  StyledAutomationHeaderTitle,
} from "../AutomationHeader/style";
import type {
  IntegrationDescription,
  TriggerConfig,
  TriggerDescription,
} from "@smartsuite/automation-engine-api";
import { Icon, Text, Tooltip } from "@smartsuite/react-ui/lib";
import { t } from "i18next";
import { translateIfPresent, translateTrigger } from "../../../../locales/i18n";
import { getIntegrationLogo } from "../../../../utils/integration-logo";
import * as R from "remeda";
import { TriggerMenu } from "../../../../components/TriggerMenu";
import { WHEN_BUTTON_IS_CLICKED_TRIGGER_ID } from "../../../../utils/constants";
import { GrayColors } from "@smartsuite/types";

export const TriggerSelector: React.FC<{
  integrations: IntegrationDescription[];
  trigger: TriggerConfig;
  isOpen: boolean;
  onChange: (integrationId: string, triggerId: string) => void;
  onClick: () => void;
  onClickOutside: () => void;
  automationId: string;
}> = ({
  integrations,
  trigger,
  isOpen,
  onChange,
  onClick,
  onClickOutside,
  automationId,
}) => {
  const changeTriggerRef = useRef<HTMLDivElement>(null);
  const integrationId = trigger.triggerReference?.integrationId;
  const triggerId = trigger.triggerReference?.triggerId;
  const [selectedIntegration, setSelectedIntegration] = useState<
    IntegrationDescription | undefined
  >(() => integrations.find((i) => i.integrationId === integrationId));
  const [selectedTrigger, setSelectedTrigger] = useState<
    TriggerDescription | undefined
  >(() => selectedIntegration?.triggers.find((t) => t.triggerId === triggerId));
  const [open, setOpen] = useState<boolean>(isOpen);
  const [allowChange, setAllowChange] = useState<boolean>(true);

  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  useEffect(() => {
    setSelectedIntegration(
      integrations.find((i) => i.integrationId === integrationId),
    );
  }, [integrationId, integrations, triggerId]);

  useEffect(() => {
    setSelectedTrigger(
      selectedIntegration?.triggers.find((t) => t.triggerId === triggerId),
    );
    if (triggerId === WHEN_BUTTON_IS_CLICKED_TRIGGER_ID) {
      setAllowChange(automationId === "");
    }
  }, [selectedIntegration, triggerId]);

  const hasSelectedTrigger =
    R.isTruthy(selectedIntegration) && R.isTruthy(selectedTrigger);

  return (
    <StyledAutomationHeader>
      <StyledActiveTriggerLogoWrapper>
        {R.isTruthy(selectedIntegration) &&
          getIntegrationLogo(selectedIntegration.integrationId)({
            width: 18,
            height: 18,
          })}
      </StyledActiveTriggerLogoWrapper>
      <StyledAutomationHeaderBody>
        <StyledAutomationHeaderText>
          <StyledAutomationHeaderTitle>
            {hasSelectedTrigger ? (
              <>
                <StyledIntegrationLabel>
                  {selectedIntegration.label}:&nbsp;
                </StyledIntegrationLabel>
                <StyledActionLabel>
                  {translateTrigger(selectedTrigger)}
                </StyledActionLabel>
              </>
            ) : (
              t("automation-form.trigger-selector")
            )}
          </StyledAutomationHeaderTitle>
          {R.isTruthy(selectedTrigger) &&
            R.isTruthy(selectedTrigger.subLabel) && (
              <Text size="m" color="secondary">
                {translateIfPresent(
                  `triggers.${selectedTrigger.subLabel}`,
                  selectedTrigger.subLabel,
                )}
              </Text>
            )}
        </StyledAutomationHeaderText>
        {hasSelectedTrigger && (
          <StyledAutomationHeaderActions>
            {allowChange ? (
              <StyledButtonOpenTriggerActionMenu
                ref={changeTriggerRef}
                onClick={onClick}
              >
                {t("general.change-trigger")}
              </StyledButtonOpenTriggerActionMenu>
            ) : (
              <StyledButtonDisabledTriggerActionMenu>
                <span>{t("automation-form.trigger-change-disabled")}</span>
                <Tooltip
                  text={t("automation-form.trigger-change-disabled-tooltip")}
                  placements={["top"]}
                  target={
                    <Icon
                      size={"XS"}
                      name={"info-baloon"}
                      color={GrayColors["G-7"]}
                    />
                  }
                />
              </StyledButtonDisabledTriggerActionMenu>
            )}
          </StyledAutomationHeaderActions>
        )}
      </StyledAutomationHeaderBody>
      <TriggerMenu
        menuButtonRef={changeTriggerRef as MutableRefObject<HTMLElement>}
        isMenuOpen={open}
        setMenuOpen={setOpen}
        onTriggerChange={(integrationId, triggerId) => {
          onChange(integrationId, triggerId);
        }}
        dropdownMaxWidth={300}
        dropdownMinWidth={300}
      />
    </StyledAutomationHeader>
  );
};
